import React, { useState } from 'react';
import { Image as ImageIcon, Film, X, Link2, FolderOpen } from 'lucide-react';
import MediaLibraryModal from '../../Modals/MediaLibraryModal';

export default function MediaPickerField({
  value,
  onChange,
  label,
  placeholder = 'Select or enter URL',
  acceptTypes = 'all', // 'all', 'image', 'video'
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputMode, setInputMode] = useState('library'); // 'library' or 'url'
  const [urlInput, setUrlInput] = useState('');

  const handleSelect = (media) => {
    onChange(media.public_url);
    setIsModalOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setUrlInput('');
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
    }
  };

  const handleUrlKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const isImage = value && (
    value.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i) ||
    value.includes('image')
  );

  const isVideo = value && (
    value.match(/\.(mp4|webm|mov)(\?|$)/i) ||
    value.includes('video')
  );

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-gray-700">{label}</label>
      )}

      {/* Mode Toggle */}
      <div className="flex border rounded-lg overflow-hidden text-xs">
        <button
          type="button"
          onClick={() => setInputMode('library')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${
            inputMode === 'library'
              ? 'bg-blue-50 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <FolderOpen className="w-3.5 h-3.5" />
          Library
        </button>
        <button
          type="button"
          onClick={() => setInputMode('url')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${
            inputMode === 'url'
              ? 'bg-blue-50 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Link2 className="w-3.5 h-3.5" />
          URL
        </button>
      </div>

      {/* Current Value Preview */}
      {value && (
        <div className="relative group">
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            {isImage ? (
              <img
                src={value}
                alt="Selected media"
                className="w-full h-32 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : isVideo ? (
              <div className="w-full h-32 flex items-center justify-center bg-gray-200">
                <Film className="w-8 h-8 text-gray-400" />
              </div>
            ) : (
              <div className="w-full h-32 flex items-center justify-center bg-gray-200">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="hidden w-full h-32 items-center justify-center bg-gray-200">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          </div>

          {/* Clear button */}
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* URL display */}
          <div className="p-2 bg-white border-t">
            <p className="text-xs text-gray-500 truncate" title={value}>
              {value}
            </p>
          </div>
        </div>
      )}

      {/* Input based on mode */}
      {inputMode === 'library' ? (
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          {value ? 'Change from Library' : 'Choose from Library'}
        </button>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput || value || ''}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={handleUrlKeyDown}
            onBlur={handleUrlSubmit}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {/* Media Library Modal */}
      <MediaLibraryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleSelect}
        acceptTypes={acceptTypes}
        currentUrl={value}
      />
    </div>
  );
}
